import * as React from 'react';
import {ThemeProvider, useTheme} from '@volkswagen-onehub/components-core';

import {BulletPagination} from './bullet-pagination';
import {ChildContainerTabIndexManager} from '../highlight-gallery-v3/tabindex-manager';
import {
    StyledCaption,
    StyledCaptionWrapper,
    StyledCarouselInner,
    StyledCarouselItem,
    StyledCarouselItemFocusWrap,
    StyledSimpleCarousel,
    StyledSimpleCarouselWrapper
} from './styled.components';

import {calcPosition} from './helpers';
import {
    useCarouselIds,
    usePaginationData,
    useTouchHandlers,
    useUpdateGalleryOnLentilClick
} from './hooks';
import {MobileCarouselProps, MobileCarouselState} from './types';
import {CarouselItemContextProvider} from './context';

const InternalMobileCarousel = (
    props: MobileCarouselProps
): React.ReactElement | null => {
    const {
        activeSlideIndex,
        altTextList,
        areInactiveSlidesOpaque = false,
        captions,
        carouselId,
        children,
        defaultPaginationItemLabel,
        galleryLabel,
        getXOfYLabel,
        hideNextSlide = false,
        isWideSlide = false,
        maxHeight,
        onSlideChange,
        paginationItemLabels,
        paginationLabel,
        renderControls,
        renderPagination,
        showDivider = false
    } = props;
    const {direction} = useTheme();
    const itemCount = React.Children.count(children);
    const carouselWrapperRef = React.useRef<HTMLDivElement>(null);
    const carouselRef = React.useRef<HTMLDivElement>(null);
    const [state, setState] = React.useState<MobileCarouselState>(() => ({
        position: calcPosition(
            activeSlideIndex,
            itemCount,
            hideNextSlide,
            isWideSlide,
            direction
        ),
        isIntermediatePosition: false
    }));

    const {handleSlideStart, handleSlideMove, handleSlideEnd, isScrollingRef} =
        useTouchHandlers({
            activeSlideIndex,
            carouselRef,
            itemCount,
            hideNextSlide,
            isWideSlide,
            direction,
            onSlideChange,
            state,
            setState
        });

    const {formatPanelId, formatTabId, formatCaptionId} = useCarouselIds(
        carouselId,
        captions
    );

    const paginationData = usePaginationData(itemCount, {
        itemLabels: paginationItemLabels,
        defaultLabel: defaultPaginationItemLabel,
        getXOfYLabel,
        formatTabId,
        formatPanelId
    });

    const handleCarouselItemClick = React.useCallback(
        (clickedSlideIndex: number) =>
            (event: React.SyntheticEvent<HTMLElement>) => {
                if (clickedSlideIndex === activeSlideIndex) return;
                if (!onSlideChange) return;
                if (isScrollingRef.current) return;

                event.preventDefault();
                event.stopPropagation();
                onSlideChange(clickedSlideIndex);
            },
        [activeSlideIndex, onSlideChange, isScrollingRef]
    );

    const handlePaginationItemClick = React.useCallback(
        (clickedSlideIndex: number) => {
            if (clickedSlideIndex === activeSlideIndex) return;
            if (isScrollingRef.current) return;
            if (!onSlideChange) return;

            onSlideChange(clickedSlideIndex);
        },
        [activeSlideIndex, onSlideChange, isScrollingRef]
    );

    const handleCarouselItemTouchEnd = React.useCallback(
        (clickedSlideIndex: number) =>
            (event: React.TouchEvent<HTMLElement>) => {
                if (isScrollingRef.current) return;

                handleCarouselItemClick(clickedSlideIndex)(event);
            },
        [handleCarouselItemClick, isScrollingRef]
    );

    const handleCarouselMove = React.useCallback(
        (e: React.TouchEvent<HTMLElement>) => {
            if (!carouselRef.current) return;
            handleSlideMove(carouselRef.current.clientWidth)(e);
        },
        [handleSlideMove, carouselRef]
    );

    const paginationProps = {
        paginationData,
        paginationLabel,
        activeIndex: activeSlideIndex,
        onSlideSelect: handlePaginationItemClick,
        altTextList
    };

    useUpdateGalleryOnLentilClick({
        itemCount,
        setState,
        activeSlideIndex,
        hideNextSlide,
        isWideSlide,
        direction
    });

    const renderCaptions = () =>
        captions && (
            <StyledCaptionWrapper>
                {captions.map((caption, index) => (
                    <StyledCaption
                        hidden={index !== activeSlideIndex}
                        id={formatCaptionId(index)}
                        key={`caption-${index}`}
                    >
                        {caption}
                    </StyledCaption>
                ))}
            </StyledCaptionWrapper>
        );

    const renderCarouselItems = () =>
        React.Children.map(children, (child, index) => (
            <StyledCarouselItem
                key={`carousel-item-${index}`}
                isActive={index === activeSlideIndex}
                hideNextSlide={hideNextSlide}
                itemCount={itemCount}
                isWideSlide={isWideSlide}
                areInactiveSlidesOpaque={areInactiveSlidesOpaque}
                role="tabpanel"
                id={formatPanelId(index)}
                aria-labelledby={formatTabId(index)}
                aria-describedby={formatCaptionId(index, true)}
                aria-hidden={index !== activeSlideIndex}
                onClickCapture={handleCarouselItemClick(index)}
                onTouchEndCapture={handleCarouselItemTouchEnd(index)}
            >
                <StyledCarouselItemFocusWrap>
                    <CarouselItemContextProvider
                        active={index === activeSlideIndex}
                    >
                        {child}
                    </CarouselItemContextProvider>
                </StyledCarouselItemFocusWrap>
            </StyledCarouselItem>
        ));

    return (
        <StyledSimpleCarouselWrapper
            ref={carouselWrapperRef}
            showDivider={showDivider}
            role="group"
            aria-label={galleryLabel}
            aria-roledescription="carousel"
        >
            {renderPagination ? (
                renderPagination(paginationProps)
            ) : (
                <BulletPagination {...paginationProps} />
            )}
            <StyledSimpleCarousel
                ref={carouselRef}
                onTouchStart={handleSlideStart}
                onTouchMove={handleCarouselMove}
                onTouchEndCapture={handleSlideEnd}
                tabIndex={-1}
                data-testid="simple-carousel"
            >
                <ChildContainerTabIndexManager
                    childContainerSelector={'[role="tabpanel"]'}
                    activeChildContainerSelector={`[role="tabpanel"]:nth-child(${activeSlideIndex + 1})`}
                >
                    <StyledCarouselInner
                        position={state.position}
                        itemCount={itemCount}
                        showTransition={!state.isIntermediatePosition}
                        maxHeight={maxHeight}
                    >
                        {renderCarouselItems()}
                    </StyledCarouselInner>
                </ChildContainerTabIndexManager>
                {renderControls && renderControls(paginationProps)}
            </StyledSimpleCarousel>
            {renderCaptions()}
        </StyledSimpleCarouselWrapper>
    );
};

export const MobileCarousel = (
    props: MobileCarouselProps
): React.ReactElement => (
    <ThemeProvider>
        <InternalMobileCarousel {...props} />
    </ThemeProvider>
);

export * from './bullet-pagination';
export * from './context';
export * from './hooks';
export * from './helpers';
export * from './tab-pagination';
export * from './types';
